// Minimal, dependency-free HTML sanitizer for the cross-origin preview iframe.
// `aboutUsHtml` arrives as a postMessage payload from the parent editor where it
// was authored by a contentEditable surface; the preview treats it as untrusted.
// This pass strips anything that could execute in our origin:
//   - dangerous tags (script, iframe, object, embed, form, style, link, meta, ...)
//   - on* event handler attributes
//   - href / src / xlink:href / srcdoc / formaction values that resolve to a
//     dangerous URL scheme (javascript:, vbscript:, data:*, file:)
//   - inline style attributes, except a narrow allow-list of formatting
//     properties (color, font-weight, font-style, text-decoration) with
//     values validated against a safe pattern — this is what lets the
//     sidebar's execCommand('bold' | 'italic' | 'foreColor') output survive
//     the trip (some browsers emit `<span style="...">` for those commands
//     instead of `<b>`/`<i>` tags). Every other CSS property/value is dropped.
//   - any attribute that is not on the allow-list (or data-* / aria-*)
// It preserves common formatting markup (p, strong, em, lists, headings, br, ...)
// and safe links. It is intentionally strict — escape hatch is the comment that
// callers can replace it with a vetted library if richer HTML is ever required.

const DANGEROUS_TAGS = new Set([
  'script',
  'iframe',
  'object',
  'embed',
  'form',
  'style',
  'link',
  'meta',
  'base',
  'frame',
  'frameset',
  'applet',
  'portal',
  'svg',
  'math',
]);

const ALLOWED_ATTRS = new Set([
  'href',
  'src',
  'alt',
  'title',
  'class',
  'id',
  'name',
  'rel',
  'target',
  'colspan',
  'rowspan',
  'scope',
  'headers',
  'datetime',
  'lang',
  'dir',
  'cite',
  'start',
  'type',
]);

function isAllowedAttr(name: string): boolean {
  if (ALLOWED_ATTRS.has(name)) return true;
  if (name.startsWith('data-')) return true;
  if (name === 'role' || name.startsWith('aria-')) return true;
  return false;
}

function isUnsafeUrl(value: string): boolean {
  const cleaned = value.replace(/[\u0000-\u001f\u007f-\u009f\s]/g, '').toLowerCase();
  if (cleaned.startsWith('javascript:')) return true;
  if (cleaned.startsWith('vbscript:')) return true;
  if (cleaned.startsWith('file:')) return true;
  if (/^data:(?!image\/(png|jpe?g|gif|webp|svg\+xml);)/i.test(cleaned)) return true;
  return false;
}

// Rich-text formatting properties the editor's toolbar can produce. Anything
// else (position, background, behavior, etc.) is dropped even if the value
// looks harmless — this stays an allow-list, not a denylist.
const ALLOWED_STYLE_PROPS = new Set(['color', 'font-weight', 'font-style', 'text-decoration']);

const SAFE_STYLE_VALUE: Record<string, RegExp> = {
  color:
    /^(#[0-9a-f]{3}|#[0-9a-f]{4}|#[0-9a-f]{6}|#[0-9a-f]{8}|rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)|rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(0|1|0?\.\d+)\s*\)|[a-z]+)$/i,
  'font-weight': /^(normal|bold|bolder|lighter|[1-9]00)$/i,
  'font-style': /^(normal|italic|oblique)$/i,
  'text-decoration':
    /^(none|underline|overline|line-through)(\s+(none|underline|overline|line-through))*$/i,
};

function isSafeStyleValue(prop: string, value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  // Belt-and-suspenders: none of the allow-listed properties legitimately
  // need parens, urls, or statement separators, so reject those outright
  // before checking the per-property pattern.
  if (/url\(|expression\(|javascript:|[;{}]/i.test(trimmed)) return false;
  const pattern = SAFE_STYLE_VALUE[prop];
  return pattern ? pattern.test(trimmed) : false;
}

// Keeps only the allow-listed formatting declarations from a `style`
// attribute value; drops everything else (including malformed declarations).
function sanitizeStyleValue(style: string): string {
  return style
    .split(';')
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .map((declaration) => {
      const separatorIndex = declaration.indexOf(':');
      if (separatorIndex === -1) return null;
      const prop = declaration.slice(0, separatorIndex).trim().toLowerCase();
      const value = declaration.slice(separatorIndex + 1).trim();
      if (!ALLOWED_STYLE_PROPS.has(prop) || !isSafeStyleValue(prop, value)) return null;
      return `${prop}: ${value}`;
    })
    .filter((declaration): declaration is string => declaration !== null)
    .join('; ');
}

function normalizeFontColorAttr(child: Element): void {
  if (child.tagName.toLowerCase() !== 'font') return;
  const color = child.getAttribute('color');
  if (color === null) return;
  child.removeAttribute('color');
  if (!isSafeStyleValue('color', color)) return;
  const existingStyle = child.getAttribute('style') || '';
  child.setAttribute('style', `${existingStyle ? `${existingStyle}; ` : ''}color: ${color.trim()}`);
}

function sanitizeNode(node: Element): void {
  const children = Array.from(node.children);
  for (const child of children) {
    const tag = child.tagName.toLowerCase();
    if (DANGEROUS_TAGS.has(tag)) {
      child.remove();
      continue;
    }
    normalizeFontColorAttr(child);
    for (const attr of Array.from(child.attributes)) {
      const name = attr.name.toLowerCase();
      const value = attr.value;
      if (name.startsWith('on')) {
        child.removeAttribute(attr.name);
      } else if (name === 'style') {
        const safeStyle = sanitizeStyleValue(value);
        if (safeStyle) {
          child.setAttribute('style', safeStyle);
        } else {
          child.removeAttribute(attr.name);
        }
      } else if (
        name === 'href' ||
        name === 'src' ||
        name === 'xlink:href' ||
        name === 'srcdoc' ||
        name === 'formaction' ||
        name === 'background'
      ) {
        if (isUnsafeUrl(value)) {
          child.removeAttribute(attr.name);
        }
      } else if (name === 'target') {
        if (value !== '_self' && value !== '_blank') {
          child.removeAttribute(attr.name);
        }
      } else if (!isAllowedAttr(name)) {
        child.removeAttribute(attr.name);
      }
    }
    sanitizeNode(child);
  }
}

export function sanitizeAboutHtml(html: string): string {
  if (!html) return '';
  // The preview receives its content in the browser. During SSR there is no
  // DOM parser, so fail closed instead of attempting incomplete regex sanitization.
  if (typeof DOMParser === 'undefined') return '';
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  sanitizeNode(doc.body);
  return doc.body.innerHTML;
}
